/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {ChangeEvent, useCallback, useMemo, useState} from 'react';
import {
  FilterChecklistOptionBase,
  FilterChecklistValueBase
} from './types';
import {
  getFilterChecklistSelectedFirstOptions,
  getFilterChecklistSelectedLookup,
  getFilterChecklistSelectedOptions,
  getFilterChecklistSortedOptions,
  getFilterChecklistValueKey,
  getFilterChecklistVisibleOptions
} from './utils';

export const DEFAULT_FILTER_CHECKLIST_VISIBLE_COUNT = 10;

interface UseFilterChecklistParams<
  T extends FilterChecklistOptionBase = FilterChecklistOptionBase
> {
  getOptionLabel: (option: T) => string;
  getOptionValue: (option: T) => FilterChecklistValueBase;
  onChange?: (selectedValues: FilterChecklistValueBase[], selectedOptions: T[]) => void;
  options: T[];
  selectedValues: FilterChecklistValueBase[];
  visibleCount?: number;
}

/**
 * Tracks search, visible options, and selected values for a filter checklist.
 *
 * ```typescript
 * import useFilterChecklist from '@ion/dls/src/filters/useFilterChecklist';
 *
 * ...
 *
 * const checklist = useFilterChecklist({
 *   getOptionLabel,
 *   getOptionValue,
 *   options,
 *   selectedValues,
 *   onChange
 * });
 * ```
 */
export function useFilterChecklist<
  T extends FilterChecklistOptionBase = FilterChecklistOptionBase
>({
  getOptionLabel,
  getOptionValue,
  onChange,
  options,
  selectedValues,
  visibleCount = DEFAULT_FILTER_CHECKLIST_VISIBLE_COUNT
}: UseFilterChecklistParams<T>) {
  const [searchValue, setSearchValue]   = useState('');
  const [searchActive, setSearchActive] = useState(false);
  const [showAll, setShowAll]           = useState(false);

  const selectedLookup = useMemo(() => (
    getFilterChecklistSelectedLookup(selectedValues)
  ), [selectedValues]);

  const sortedOptions = useMemo(() => (
    getFilterChecklistSortedOptions(options, getOptionLabel)
  ), [getOptionLabel, options]);

  const visibleOptions = useMemo(() => (
    getFilterChecklistVisibleOptions(sortedOptions, searchValue, getOptionLabel)
  ), [getOptionLabel, searchValue, sortedOptions]);

  const orderedVisibleOptions = useMemo(() => (
    getFilterChecklistSelectedFirstOptions(
      visibleOptions,
      selectedValues,
      getOptionValue
    )
  ), [getOptionValue, selectedValues, visibleOptions]);

  // only a typed term searches - opening the search box keeps the list as
  // it was (its first batch, or every option after 'show all') ...
  const searching = !!searchValue.trim();

  const displayedOptions = useMemo(() => (
    searching || showAll
      ? orderedVisibleOptions
      : orderedVisibleOptions.slice(0, visibleCount)
  ), [orderedVisibleOptions, searching, showAll, visibleCount]);

  const hasOverflow        = options.length > visibleCount;
  const allVisible         = showAll || !hasOverflow;
  const hasSelections      = !!selectedValues.length;
  const allVisibleSelected = !!visibleOptions.length && visibleOptions.every(option => (
    selectedLookup[getFilterChecklistValueKey(getOptionValue(option))]
  ));

  const activateSearch = useCallback(() => {
    setSearchActive(true);
  }, []);

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchActive(true);
    setSearchValue(event.target.value);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchValue('');
  }, []);

  const closeSearch = useCallback(() => {
    setSearchActive(false);
    setSearchValue('');
  }, []);

  // leaving an empty search box ends the search - a typed term stays
  // active (with its results) until it is cleared ...
  const blurSearch = useCallback(() => {
    if (!searchValue.trim()) {
      setSearchActive(false);
      setSearchValue('');
    }
  }, [searchValue]);

  const toggleShowAll = useCallback(() => {
    setShowAll(currentShowAll => !currentShowAll);
  }, []);

  const handleSelectAll = useCallback(() => {
    const visibleValues  = visibleOptions.map(option => getOptionValue(option));
    const visibleLookup  = getFilterChecklistSelectedLookup(visibleValues);
    const retainedValues = searchActive ? selectedValues.filter(value => (
      !visibleLookup[getFilterChecklistValueKey(value)]
    )) : [];
    const nextSelectedValues  = [...retainedValues, ...visibleValues];
    const nextSelectedOptions = getFilterChecklistSelectedOptions(
      sortedOptions,
      nextSelectedValues,
      getOptionValue
    );
    onChange?.(nextSelectedValues, nextSelectedOptions);
  }, [getOptionValue, onChange, searchActive, selectedValues, sortedOptions, visibleOptions]);

  const handleClear = useCallback(() => {
    onChange?.([], []);
  }, [onChange]);

  const toggleOption = useCallback((option: T, checked: boolean) => {
    const optionValue = getOptionValue(option);
    const nextSelectedValues = checked
      ? [
        optionValue,
        ...selectedValues.filter(value => getFilterChecklistValueKey(value) !==
          getFilterChecklistValueKey(optionValue))
      ]
      : selectedValues.filter(value => getFilterChecklistValueKey(value) !==
        getFilterChecklistValueKey(optionValue));
    const nextSelectedOptions = getFilterChecklistSelectedOptions(
      options,
      nextSelectedValues,
      getOptionValue
    );
    onChange?.(nextSelectedValues, nextSelectedOptions);
  }, [getOptionValue, onChange, options, selectedValues]);

  return {
    activateSearch,
    allVisible,
    allVisibleSelected,
    blurSearch,
    clearSearch,
    closeSearch,
    displayedOptions,
    handleClear,
    handleSearchChange,
    handleSelectAll,
    hasOverflow,
    hasSelections,
    searchActive,
    searching,
    searchValue,
    selectedLookup,
    toggleShowAll,
    toggleOption,
    visibleOptions
  };
}

export default useFilterChecklist;
